import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ContentCopy,
  Edit,
  NotInterested,
  Restore,
  Star,
  StarBorder,
  PlaylistRemove,
} from '@mui/icons-material';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
  IListQueryParms,
  queryKeyMessageRequestList,
  useRqGetMessageList,
  useRqAllGetMessageList,
  queryKeyAllMessageRequestApiPath,
} from '../../../react_query/messages/MessagesByCmpId';
import { DataGridCustPagination } from '../../../components/utlis/DataGrid/Pagination';
import { useStoreSelector } from '../../../store/configstore';
import { companyIdSelector } from '../../../store/Slices/companySlice';
import { useUserCanAny } from '../../../hooks/useUserCan';
import { GridCellExpand } from '../../../components/utlis/DataGrid/ExpandCellRender';
import CustomNoRowsOverlay from '../../../components/utlis/DataGrid/CustomNoRowsOverlay';
import IconAdd from '../../../asset/icons/icon_messaging.svg';
import IconAddEvent from '../../../asset/icons/icon_addsong.svg';
import IconViewButton from '../../../components/Button/IconViewButton';
import { UserCanAny } from '../../../components/UserCan';
import { TimedMsgDeActivate } from './TimedMsgRestoreDeActive';
import TimedMessageCreate from './TimedMessageCreate';
import DeleteRow from '../../../components/utlis/DataGrid/DeleteRow';
import { apiPut } from '../../../axiosConfig';
import DialogApiRequestStatus from '../../../components/dialogs/DialogApiRequestStatus';
import notificationConfig from '../../../config/notificationConfig';
import MusicPreviewBtn from '../../../components/music/MusicPreviewBtn';

const TimedMsgDataGrid = ({
  search,
  query,
  setViewId,
  setQuery,
  toggleCreateDialog,
  status,
  setStatus,
  repository,
}: {
  search: string;
  query: IListQueryParms;
  setViewId: (val: number) => void;
  toggleCreateDialog: () => void;
  setQuery: React.Dispatch<React.SetStateAction<IListQueryParms>>;
  status: string;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  repository: boolean;
}) => {
  const company_id = useStoreSelector(companyIdSelector);
  const queryClient = useQueryClient();
  const [pagination, setPagination] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const canEdit = useUserCanAny(
    'is-admin,partner,timed-message:view,timed-message:update,timed-message:delete,timed-message:create',
  );

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Message Name',
      hideable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const hasClosureIcon = params.row.is_closure === 1;
        return (
          <Box className='flex items-center gap-2 w-full'>
            <Typography variant='body2' className='ellipsis' sx={{ minWidth: 0 }}>
              {params.row.name}
            </Typography>
            {hasClosureIcon && (
              <PlaylistRemove
                fontSize='small'
                className='text-orange-variant4 no-shrink'
                titleAccess='Closure Time Message'
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'start_times',
      headerName: 'Message Time',
      hideable: false,
      flex: 1,
    },
    {
      field: 'created_at',
      headerName: 'Created Date',
      hideable: false,
      width: 178,
      flex: 0.6,
      renderCell: (params: GridRenderCellParams) => {
        return dayjs(params.value).format('MM/DD/YYYY');
      },
    },
    {
      field: 'created_by',
      headerName: 'Created By',
      hideable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <GridCellExpand
            value={params.row.created_by.name}
            width={params.colDef.computedWidth}
          />
        );
      },
    },
    {
      field: 'start_date',
      headerName: 'Start Date',
      hideable: false,
      width: 120,
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        return dayjs(params.value).format('MM/DD/YYYY');
      },
    },
    {
      field: 'end_date',
      headerName: 'End Date',
      hideable: false,
      width: 120,
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => {
        return dayjs(params.value).format('MM/DD/YYYY');
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      hideable: false,
      width: 150,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Action',
      width: 200,
      sortable: false,
      flex: 1,
      cellClassName: 'justify-center',
      renderCell: (params: GridRenderCellParams) => (
        <ActionDetails
          params={params}
          setViewId={setViewId}
          setStatus={setStatus}
          repository={repository}
        />
      ),
    },
  ].map(column => ({
    ...column,
    filterable: false,
  }));

  React.useEffect(() => {
    setQuery(prev => {
      return {
        ...prev,
        page: pagination.page + 1,
        per_page: pagination.pageSize,
        search: search,
        status,
      };
    });
  }, [company_id, pagination, search, setQuery, status]);
  const activeState = !!(Number(company_id) && Number(pagination.page + 1));

  const { data, isLoading } = useRqGetMessageList(
    Number(company_id),
    query,
    'timed-message',
    activeState,
  );

  const handleSortModelChange = (model: GridSortModel) => {
    if (model[0]) {
      setQuery(prev => ({ ...prev, ...model[0] }));
    }
  };

  const { data: allTimedMsg, isLoading: isAllLoading } = useRqAllGetMessageList(
    Number(company_id),
    query,
    'timed-message',
  );
  useEffect(() => {
    if (repository) {
      queryClient.invalidateQueries({
        queryKey: [
          queryKeyAllMessageRequestApiPath(company_id, 'timed-message'),
        ],
      });
    }
  }, [repository, company_id, queryClient]);

  return (
    <Box sx={{ height: 'calc(100vh - 250px)' }} mt={1}>
      <DataGrid
        experimentalFeatures={{ ariaV7: true }}
        disableColumnMenu
        disableColumnSelector
        className='custom-data-grid cmc_new_grid h-654 text-white'
        rows={repository ? allTimedMsg?.data ?? [] : data?.data ?? []}
        columns={columns}
        loading={repository ? isAllLoading : isLoading}
        rowCount={
          repository ? allTimedMsg?.meta?.total ?? 0 : data?.meta?.total ?? 0
        }
        paginationModel={pagination}
        pageSizeOptions={[10, 25, 50]}
        paginationMode='server'
        onPaginationModelChange={setPagination}
        onSortModelChange={handleSortModelChange}
        columnVisibilityModel={{
          request_type: true,
          actions: canEdit,
        }}
        disableRowSelectionOnClick
        slots={{
          pagination: DataGridCustPagination,
          noRowsOverlay: () => (
            <CustomNoRowsOverlay
              imageSrc={IconAdd}
              message='No timed messages available'
              buttonText='Create New'
              IconButton={IconAddEvent}
              onButtonClick={toggleCreateDialog}
            />
          ),
        }}
        slotProps={{
          columnsPanel: {
            disableHideAllButton: true,
            disableShowAllButton: true,
          },
        }}
      />
    </Box>
  );
};

const ActionDetails = ({
  params,
  setViewId,
  setStatus,
  repository,
}: {
  params: GridRenderCellParams;
  setViewId: (val: number) => void;
  setStatus: React.Dispatch<React.SetStateAction<string>>;
  repository: boolean;
}) => {
  const queryClient = useQueryClient();
  const [dialogType, setDialogType] = useState('');
  const companyId = useSelector(companyIdSelector);
  const status = params.row.status;
  // Build scopes dynamically
  const scopes = [
    'is-admin',
    'timed-message:update',
    status !== 'In Progress' ? 'partner' : null,
  ]
    .filter(Boolean)
    .join(',');
  const [apiState, setApiState] = useState({
    status: '',
    error: null,
    message: '',
  });

  const handleToggleFavourite = (
    id: number,
    currentValue: boolean | number,
  ) => {
    setApiState({ status: 'pending', error: null, message: '' });
    const newValue = !Boolean(currentValue) ? 1 : 0;
    if (!id) return;

    apiPut(
      `/v1/company/${companyId}/message/timed-message/updateTimedMessage/${id}`,
      { favourite: newValue },
    )
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [
            queryKeyAllMessageRequestApiPath(companyId, 'timed-message'),
          ],
          refetchType: 'active',
        });
        setApiState({
          status: 'success',
          error: null,
          message: 'Favourite status updated successfully.',
        });
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
          message: 'Failed to update favourite status.',
        });
      });
  };

  return (
    <Stack
      className='w-full'
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      spacing={1}>
      <Box
        className='flex items-center justify-center w-full'
        sx={{ gap: '8px' }}>
        <UserCanAny scope='is-admin,partner,timed-message:view'>
          <IconViewButton
            onClick={() => setViewId(params.row.id)}
            title='View'
          />
        </UserCanAny>
        <UserCanAny scope={scopes}>
          {repository ? (
            <IconButton
              aria-label='create'
              color='primary'
              title='Create New'
              onClick={() => setDialogType('duplicate')}>
              <img src={IconAdd} alt='Create' width={20} />
            </IconButton>
          ) : (
            <IconButton
              aria-label='edit'
              color='primary'
              title='Edit'
              onClick={() => setDialogType('update')}>
              <Edit />
            </IconButton>
          )}
        </UserCanAny>

        {repository && (
          <IconButton
            aria-label='favourite'
            color={params.row.favourite ? 'warning' : 'default'}
            title={params.row.favourite ? 'Unfavourite' : 'Mark as Favourite'}
            onClick={() =>
              handleToggleFavourite(params.row.id, params.row.favourite)
            }>
            {params.row.favourite ? (
              <Star className='yellow-color' />
            ) : (
              <StarBorder />
            )}
          </IconButton>
        )}

        {!repository && (
          <>
            <UserCanAny scope='is-admin,timed-message:delete'>
              <DeleteRow
                dialog_content={
                  <>
                    <Typography paragraph variant='body1'>
                      {`${notificationConfig.deleteConfirmText} "${params.row.name}"`}
                    </Typography>
                    <Typography
                      paragraph
                      variant='body1'>{`${notificationConfig.note}`}</Typography>
                  </>
                }
                refreshQueryKey={queryKeyMessageRequestList(
                  companyId,
                  'timed-message',
                )}
                url={`v1/company/${companyId}/message/timed-message/${params.row.id}`}
              />
            </UserCanAny>

            {params.row.status !== 'In Progress' && (
              <>
                <UserCanAny scope='is-admin,partner,timed-message:update'>
                  {params.row.status === 'Inactive' ? (
                    <IconButton
                      aria-label='close'
                      color='primary'
                      title='Restore'
                      onClick={() => setDialogType('restore')}>
                      <Restore />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label='close'
                      color='error'
                      title='Deactivate'
                      onClick={() => setDialogType('de-activate')}>
                      <NotInterested />
                    </IconButton>
                  )}
                </UserCanAny>

                <UserCanAny scope='is-admin,partner,timed-message:create'>
                  <IconButton
                    onClick={() => setDialogType('duplicate')}
                    title='Duplicate'>
                    <ContentCopy />
                  </IconButton>
                </UserCanAny>
              </>
            )}
          </>
        )}
      </Box>

      {dialogType !== 'de-activate' && dialogType !== '' && (
        <TimedMessageCreate
          onClose={() => setDialogType('')}
          timedId={Number(params.row.id)}
          type={dialogType}
          setStatus={setStatus}
          repository={repository}
        />
      )}
      {dialogType === 'de-activate' && (
        <TimedMsgDeActivate
          request_id={Number(params.row.id)}
          handleCancel={() => setDialogType('')}
        />
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={() =>
            handleToggleFavourite(params.row.id, params.row.favourite)
          }
          onEdit={() => setApiState({ status: '', error: null, message: '' })}
          onClose={() => setApiState({ status: '', error: null, message: '' })}
        />
      )}
      {params.row.media_track_url ? (
        <MusicPreviewBtn
          id={params.row.media_track_id}
          url={params.row.media_track_url}
          track_name={params.row.media_track_name}
          track_artist={params.row.media_track_artist}
        />
      ) : null}
    </Stack>
  );
};

export default TimedMsgDataGrid;
